import { Link } from "react-router-dom";
import SEO from "./SEO";
import { Reveal, Eyebrow } from "./Reveal";

const ORIGIN = window.location.origin;

const InlineLink = ({ to, children }) => (
  <Link to={to} className="text-[#C5A059] underline decoration-[#C5A059]/40 underline-offset-4 hover:decoration-[#C5A059] transition-colors">
    {children}
  </Link>
);

const P = ({ children }) => (
  <p className="text-sm sm:text-base text-[#94A3B8] leading-loose mt-5">{children}</p>
);

export default function SeoLandingLayout({
  testId,
  path,
  seoTitle,
  seoDescription,
  crumbLabel,
  eyebrow,
  h1,
  intro = [],
  hero,
  sections = [],
  faqs = [],
  cta,
  siblings = [],
}) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${ORIGIN}${path}`,
        name: seoTitle,
        description: seoDescription,
        url: `${ORIGIN}${path}`,
        isPartOf: { "@type": "WebSite", name: "RK Group", url: ORIGIN },
        about: {
          "@type": "JewelryStore",
          name: "Ramesh Kapoor and Sons Jewellers Pvt. Ltd.",
          url: `${ORIGIN}/jewellery-meerut`,
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: ORIGIN },
          { "@type": "ListItem", position: 2, name: "Jewellery", item: `${ORIGIN}/jewellery` },
          { "@type": "ListItem", position: 3, name: crumbLabel, item: `${ORIGIN}${path}` },
        ],
      },
      ...(faqs.length
        ? [
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ]
        : []),
    ],
  };

  return (
    <div data-testid={testId} className="pt-28 sm:pt-36">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={`${ORIGIN}${path}`}
        robots="index, follow"
        jsonLd={jsonLd}
      />

      <nav aria-label="Breadcrumb" className="max-w-[900px] mx-auto px-5 sm:px-8 text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
        <ol className="flex flex-wrap items-center gap-2">
          <li><Link to="/" className="hover:text-[#C5A059] transition-colors">Home</Link></li>
          <li className="text-[#C5A059]/60">·</li>
          <li><Link to="/jewellery" className="hover:text-[#C5A059] transition-colors">Jewellery</Link></li>
          <li className="text-[#C5A059]/60">·</li>
          <li className="text-[#C5A059]">{crumbLabel}</li>
        </ol>
      </nav>

      <header className="max-w-[900px] mx-auto px-5 sm:px-8 pt-10 pb-14">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="font-serif font-light text-4xl sm:text-6xl tracking-tight text-[#FBF9F5] mt-4 leading-[1.05]">
          {h1}
        </h1>
        {intro.map((p, i) => (
          <P key={i}>{p}</P>
        ))}
      </header>

      <div className="max-w-[900px] mx-auto px-5 sm:px-8 pb-24">
        {hero && (
          <div className="aspect-[16/7] overflow-hidden border border-white/10 mb-4">
            <img src={hero.src} alt={hero.alt} className="w-full h-full object-cover" loading="eager" />
          </div>
        )}

        {sections.map((s, si) => (
          <section key={s.id} id={s.id} className="border-t border-white/10 py-16 sm:py-20" data-testid={`${testId}-section-${s.id}`}>
            <Reveal>
              <Eyebrow>{s.eyebrow}</Eyebrow>
              <h2 className="font-serif font-light text-3xl sm:text-4xl text-[#FBF9F5] tracking-tight mt-4">{s.title}</h2>
              {s.image ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 items-start mt-2">
                  <div className={si % 2 ? "sm:order-2" : ""}>
                    {s.paragraphs.map((p, i) => (
                      <P key={i}>{p}</P>
                    ))}
                  </div>
                  <div className={`aspect-[4/3] overflow-hidden border border-white/10 ${si % 2 ? "sm:order-1" : ""}`}>
                    <img src={s.image.src} alt={s.image.alt} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                </div>
              ) : (
                s.paragraphs.map((p, i) => <P key={i}>{p}</P>)
              )}
            </Reveal>
          </section>
        ))}

        {faqs.length > 0 && (
          <section className="border-t border-white/10 py-16 sm:py-20" data-testid={`${testId}-faq`}>
            <Reveal>
              <Eyebrow>Questions</Eyebrow>
              <h2 className="font-serif font-light text-3xl sm:text-4xl text-[#FBF9F5] tracking-tight mt-4">
                Frequently Asked Questions
              </h2>
              <div className="mt-10">
                {faqs.map((f, i) => (
                  <details key={i} className="group border-t border-white/10 py-6" data-testid={`${testId}-faq-${i}`}>
                    <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                      <h3 className="font-serif text-xl sm:text-2xl text-[#FBF9F5] group-hover:text-[#C5A059] transition-colors duration-300">
                        {f.q}
                      </h3>
                      <span className="text-[#C5A059] text-2xl leading-none mt-1 transition-transform duration-300 group-open:rotate-45">+</span>
                    </summary>
                    <p className="text-sm sm:text-base text-[#94A3B8] leading-loose mt-4 max-w-3xl">{f.a}</p>
                  </details>
                ))}
              </div>
            </Reveal>
          </section>
        )}

        {cta && (
          <section className="border-t border-white/10 pt-16 pb-4 text-center">
            <Reveal>
              <h2 className="font-serif font-light text-2xl sm:text-4xl text-[#FBF9F5]">{cta.title}</h2>
              <div className="flex flex-wrap justify-center gap-4 mt-10">
                {cta.buttons.map((b, i) => (
                  <Link key={i} to={b.to} data-testid={`${testId}-cta-${i}`}
                    className={
                      b.primary
                        ? "text-[11px] uppercase tracking-[0.2em] font-semibold bg-[#C5A059] text-[#060A12] px-8 py-4 hover:bg-[#d6b678] transition-colors duration-300"
                        : "text-[11px] uppercase tracking-[0.2em] font-semibold border border-white/20 text-[#FBF9F5] px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
                    }>
                    {b.label}
                  </Link>
                ))}
              </div>
            </Reveal>
          </section>
        )}

        {siblings.length > 0 && (
          <section className="border-t border-white/10 mt-16 pt-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] font-semibold mb-5">Continue exploring</p>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {siblings.map((s) => (
                <InlineLink key={s.to} to={s.to}>{s.label}</InlineLink>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
